import { useEffect, useMemo, useState } from "react"
import { Dialog, DialogHeader, DialogBody, DialogFooter } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/cn"
import type { EscalationReason } from "@/types/agent_escalation_reason"

// Modal for "merge X into Y". Operator picks a target via a typeahead over
// every other reason in this agent. Confirming POSTs to the merge endpoint —
// the source is archived and its convo refs + timesUsed transfer to target.

type Props = {
  open: boolean
  source: EscalationReason | null
  candidates: EscalationReason[]
  onClose: () => void
  onConfirm: (targetId: number) => void
}

export function MergeReasonDialog({ open, source, candidates, onClose, onConfirm }: Props) {
  const [query, setQuery] = useState("")
  const [targetId, setTargetId] = useState<number | null>(null)

  // Reset state every time the dialog opens for a new source.
  useEffect(() => {
    if (open) { setQuery(""); setTargetId(null) }
  }, [open, source?.id])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return candidates
    return candidates.filter((c) =>
      c.name.toLowerCase().includes(q) ||
      c.key.toLowerCase().includes(q) ||
      c.description.toLowerCase().includes(q)
    )
  }, [candidates, query])

  const target = useMemo(
    () => candidates.find((c) => c.id === targetId) ?? null,
    [candidates, targetId],
  )

  const confirm = () => {
    if (!target) return
    onConfirm(target.id)
  }

  return (
    <Dialog open={open} onClose={onClose} size="md">
      <DialogHeader
        title="Merge escalation reason"
        subtitle={source ? <>Pick a target to absorb <span className="font-medium text-app-text">"{source.name}"</span>.</> : undefined}
      />
      <DialogBody>
        <Input
          type="text"
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search reasons by name or description…"
          className="w-full"
        />

        <div className="max-h-72 overflow-y-auto rounded-control border border-border">
          {filtered.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-app-muted">
              {candidates.length === 0
                ? "No other reasons exist yet — create one first, then merge."
                : "No matches."}
            </div>
          ) : (
            filtered.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setTargetId(c.id)}
                className={cn(
                  "flex w-full items-start justify-between gap-3 border-b border-border px-3 py-2 text-left text-sm transition last:border-b-0 hover:bg-white/5",
                  c.id === targetId && "bg-brand-redSoft/30 ring-1 ring-brand-red/40",
                )}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate font-medium text-app-text">{c.name}</span>
                    <span className="font-mono text-[11px] text-app-muted">{c.key}</span>
                  </div>
                  {c.description && (
                    <div className="mt-0.5 line-clamp-2 text-xs text-app-muted">{c.description}</div>
                  )}
                </div>
                <div className="flex flex-shrink-0 flex-col items-end gap-1">
                  <Badge tone={c.status === "live" ? "success" : c.status === "pending" ? "gold" : "neutral"}>
                    {c.status}
                  </Badge>
                  <span className="text-[11px] text-app-muted">{c.timesUsed} used</span>
                </div>
              </button>
            ))
          )}
        </div>

        {source && target && (
          <div className="rounded-control border border-vip-gold/30 bg-vip-goldMute/40 px-3 py-2 text-sm text-app-text">
            Merge <span className="font-medium">"{source.name}"</span> into{" "}
            <span className="font-medium">"{target.name}"</span>? Source will be archived;
            convos and <span className="font-mono">timesUsed</span> move to target.
          </div>
        )}
      </DialogBody>
      <DialogFooter>
        <button
          type="button"
          onClick={onClose}
          className="rounded-control border border-border px-4 py-2 text-sm text-app-text transition hover:bg-white/5"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={confirm}
          disabled={!target}
          className="rounded-control bg-brand-red px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-redDeep disabled:opacity-50"
        >
          Confirm merge
        </button>
      </DialogFooter>
    </Dialog>
  )
}
